/**
 * Legal & Regulatory Compliance Register.
 *
 * The data-entry and action surface behind the MIS compliance section. Two
 * things live here, and the split is deliberate:
 *
 *   OBLIGATIONS — "this requirement applies to this site, this often, owned by
 *                 this person". Applicability is per site: a boiler rule applies
 *                 to sites with a boiler, and giving every site the same
 *                 checklist is how compliance percentages stop being believed.
 *
 *   REGISTER    — one row per due period, generated by the server from the
 *                 obligation's frequency. The client never creates periods and
 *                 never sends a status: status is derived server-side from the
 *                 due and completed dates, so this page cannot show "compliant"
 *                 for a deadline that has quietly passed.
 *
 * Evidence is a Document Control id rather than an upload, and a non-compliance
 * carries a CAPA id, so remediation is tracked by the module that already owns
 * it and shows up in the MIS action tracker without extra wiring.
 *
 * Scope and authorization are the server's: it resolves the site fail-closed and
 * requires the report-create grant for every write.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, ArrowLeft, CheckCircle2, ClipboardList, FileWarning, Info,
  Loader2, Plus, RefreshCw, Trash2, X, Gavel, Layers, ShieldAlert, Sparkles,
  CalendarClock, Scale,
} from 'lucide-react';
import axios from 'axios';
import TabNavigation from '../../components/TabNavigation';
import LegalReferences from './LegalReferences';
import { useSite } from '../../context/SiteContext';
import { useAlert } from '../../context/AlertContext';
import {
  EVENT_STATUSES, FREQUENCIES, SEVERITY_META, adoptCatalogEntries,
  createObligation, deleteObligation, fetchApplicableCatalog, fetchObligations,
  fetchRegister, fetchRegisterInsights, generateEvents,
  recordEvent, updateObligation,
} from '../../services/complianceApi';

const STATUS_META = EVENT_STATUSES.reduce((acc, s) => ({ ...acc, [s.id]: s }), {});

const TONE_CHIP = {
  rose: 'bg-rose-100 text-rose-800',
  amber: 'bg-amber-100 text-amber-800',
  sky: 'bg-sky-100 text-sky-800',
  emerald: 'bg-emerald-100 text-emerald-800',
  gray: 'bg-gray-100 text-gray-600',
};

// Headline figures colour the number itself rather than a chip. Kept as literal
// class strings so Tailwind's scanner sees them.
const TONE_TEXT = {
  rose: 'text-rose-700',
  amber: 'text-amber-700',
  sky: 'text-sky-700',
  emerald: 'text-emerald-700',
  gray: 'text-gray-900',
};

const FREQUENCY_LABEL = FREQUENCIES.reduce(
  (acc, f) => ({ ...acc, [f.id]: f.label }), {},
);

// Suggestions only. The register is tenant data and no jurisdiction is
// hard-coded — a plant can type whatever category its legal list uses.
const CATEGORY_SUGGESTIONS = [
  'Air', 'Water', 'Hazardous Waste', 'Solid Waste', 'Noise',
  'Factory Licence', 'Boiler / Pressure Vessel', 'Electrical', 'Fire',
  'Labour', 'Occupational Health', 'Other',
];

const EMPTY_OBLIGATION = {
  title: '', citation: '', category: '', authority: '',
  frequency: 'annual', frequency_days: '', first_due_date: '',
  responsible_name: '', evidence_required: true, notes: '', is_active: true,
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** Days between a due date and today; negative means overdue. */
const daysUntil = (iso) => {
  if (!iso) return null;
  const due = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(due.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((due - today) / 86400000);
};

const StatusChip = ({ status }) => {
  const meta = STATUS_META[status];
  if (!meta) return <span className="text-xs text-gray-500">{status}</span>;
  return (
    <span className={`px-2 py-0.5 rounded text-[11px] font-medium whitespace-nowrap
                      ${TONE_CHIP[meta.tone] || TONE_CHIP.gray}`}>
      {meta.label}
    </span>
  );
};

/**
 * A headline number. `tone` carries the meaning: overdue is never neutral. A
 * zero stays neutral — nothing overdue is not a warning — while N/A means the
 * figure could not be computed, which is a different thing from zero.
 */
const SummaryCard = ({ label, value, tone = 'gray', hint }) => {
  const missing = value === null || value === undefined;
  const alarming = !missing && Number(value) !== 0 && tone !== 'gray';
  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-white">
      <div className="text-xs text-gray-500 leading-tight">{label}</div>
      <div className={`text-2xl font-semibold mt-1 tabular-nums ${missing
        ? 'text-gray-400' : (alarming ? TONE_TEXT[tone] : TONE_TEXT.gray)}`}>
        {missing ? 'N/A' : value}
      </div>
      {hint && <div className="text-[11px] text-gray-400 mt-0.5">{hint}</div>}
    </div>
  );
};

const Modal = ({ title, onClose, children, footer, wide }) => (
  <div className="fixed inset-0 bg-black/40 z-40 flex items-start justify-center overflow-y-auto py-10">
    <div className={`bg-white rounded-xl shadow-xl w-full mx-4 ${wide ? 'max-w-2xl' : 'max-w-lg'}`}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        <button onClick={onClose} aria-label="Close"
                className="text-gray-400 hover:text-gray-600">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="px-5 py-4">{children}</div>
      {footer && (
        <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-2">
          {footer}
        </div>
      )}
    </div>
  </div>
);

const Field = ({ label, hint, children, required }) => (
  <label className="block">
    <span className="block text-xs font-medium text-gray-600 mb-1">
      {label}{required && <span className="text-rose-600"> *</span>}
    </span>
    {children}
    {hint && <span className="block text-[11px] text-gray-400 mt-1">{hint}</span>}
  </label>
);

const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm';

/** Exposure / coverage headline for the register. */
const ExposureBand = ({ insights }) => {
  if (!insights) return null;
  const exposure = insights.exposure || {};
  const coverage = insights.coverage || null;
  const band = {
    none: { cls: 'bg-emerald-50 border-emerald-300 text-emerald-900', label: 'No exposure' },
    low: { cls: 'bg-sky-50 border-sky-300 text-sky-900', label: 'Low exposure' },
    moderate: { cls: 'bg-amber-50 border-amber-300 text-amber-900', label: 'Moderate exposure' },
    high: { cls: 'bg-rose-50 border-rose-300 text-rose-900', label: 'High exposure' },
  }[exposure.band] || { cls: 'bg-gray-50 border-gray-300 text-gray-800', label: 'Exposure' };
  return (
    <div className={`border rounded-xl p-4 mb-5 ${band.cls}`}>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5" />
          <span className="font-semibold">{band.label}</span>
        </div>
        <div className="text-sm">
          {Object.entries(exposure.by_severity || {}).map(([sev, v]) => (
            <span key={sev} className="mr-4">
              {SEVERITY_META[sev]?.label || sev}: {v.overdue} overdue
              {v.breached ? `, ${v.breached} breached` : ''}
            </span>
          ))}
          {!Object.keys(exposure.by_severity || {}).length && (
            <span>Nothing overdue or breached</span>
          )}
        </div>
        {coverage && (
          // Coverage sits next to exposure on purpose: a compliance rate means
          // nothing without the share of applicable requirements it is a rate of.
          // A site that has not answered the applicability questions has UNKNOWN
          // coverage, which is not 0% — saying 0 would be as wrong as saying 100.
          <div className="text-sm ml-auto">
            {coverage.coverage_pct === null || coverage.coverage_pct === undefined ? (
              <span>
                Coverage not established —{' '}
                <span className="font-medium underline">
                  answer what applies to this site
                </span>
              </span>
            ) : (
              <>
                Register covers{' '}
                <span className="font-semibold">{coverage.coverage_pct}%</span>
                {' '}of what applies ({coverage.tracked} of {coverage.applicable})
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/** Ranked findings about the register itself. */
const InsightFindings = ({ insights, onJumpToCatalog }) => {
  const findings = insights?.findings || [];
  if (!findings.length) return null;
  const chip = { critical: 'bg-rose-600 text-white', warning: 'bg-amber-500 text-white',
                 watch: 'bg-slate-400 text-white' };
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-5">
      <div className="px-5 py-3 border-b border-gray-200 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-blue-600" />
        <h2 className="font-semibold text-gray-900">What this register is telling you</h2>
      </div>
      <ul className="divide-y divide-gray-100">
        {findings.map((f, i) => {
          const isGap = /not being tracked|not in the register/.test(f.title);
          return (
            <li key={i} className="px-5 py-2.5 flex items-start gap-3">
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold mt-0.5
                                whitespace-nowrap ${chip[f.severity] || chip.watch}`}>
                {f.severity === 'critical' ? 'Critical'
                  : f.severity === 'warning' ? 'Action' : 'Watch'}
              </span>
              <span className="flex-1">
                <span className="text-sm text-gray-900">{f.title}</span>
                <span className="block text-xs text-gray-500">{f.detail}</span>
              </span>
              {isGap && (
                <button onClick={onJumpToCatalog}
                        className="text-xs font-medium text-blue-600 hover:text-blue-800
                                   whitespace-nowrap">
                  Review what applies →
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

/** The next 90 days and the categories carrying the overdue load. */
const LookaheadPanels = ({ insights }) => {
  const upcoming = insights?.upcoming || [];
  const categories = (insights?.by_category || []).filter((c) => c.total > 0).slice(0, 6);
  if (!upcoming.length && !categories.length) return null;
  const worst = Math.max(1, ...categories.map((c) => c.total));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <div className="flex items-center gap-2 mb-3">
          <CalendarClock className="h-4 w-4 text-gray-500" />
          <h3 className="font-semibold text-gray-900">
            Next {insights.lookahead_days} days
          </h3>
        </div>
        {upcoming.length === 0 ? (
          <p className="text-sm text-gray-500">Nothing falls due in this window.</p>
        ) : (
          <ul className="space-y-2">
            {upcoming.map((u) => (
              <li key={u.period} className="flex items-center gap-3">
                <span className="text-sm text-gray-700 w-24">{u.period}</span>
                <span className="flex-1 h-2 bg-gray-100 rounded">
                  <span className="block h-2 bg-blue-500 rounded"
                        style={{ width: `${Math.min(100, u.due * 12)}%` }} />
                </span>
                <span className="text-sm tabular-nums text-gray-900 w-8 text-right">
                  {u.due}
                </span>
                {u.critical > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100
                                   text-rose-800 whitespace-nowrap">
                    {u.critical} critical
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Layers className="h-4 w-4 text-gray-500" />
          <h3 className="font-semibold text-gray-900">Where the load sits</h3>
        </div>
        <ul className="space-y-2">
          {categories.map((c) => (
            <li key={c.category} className="flex items-center gap-3">
              <span className="text-sm text-gray-700 flex-1 truncate">{c.category}</span>
              <span className="w-28 h-2 bg-gray-100 rounded overflow-hidden flex">
                <span className="h-2 bg-rose-500"
                      style={{ width: `${(c.overdue / worst) * 100}%` }} />
                <span className="h-2 bg-gray-300"
                      style={{ width: `${((c.total - c.overdue) / worst) * 100}%` }} />
              </span>
              <span className="text-xs tabular-nums text-gray-500 w-20 text-right">
                {c.overdue} / {c.total}
              </span>
            </li>
          ))}
        </ul>
        <p className="text-[11px] text-gray-400 mt-3">
          Overdue against total periods, by category.
        </p>
      </div>
    </div>
  );
};

/**
 * Statutes the catalog suggests, with this tenant's industry highlighted.
 * Mirrors "What applies to us": a proposal with its reasons shown, adopted
 * explicitly, never applied on the user's behalf. Adopting is gated on the
 * legal-admin roles server-side; can_manage comes back from the same call so
 * the button is not offered to someone the API would reject.
 */
const SuggestedReferences = ({ siteId, onAdopted }) => {
  const { showError, showSuccess } = useAlert();
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/legal-references/catalog`,
        { params: siteId ? { site_id: siteId } : {},
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setData(d);
    } catch (e) {
      showError(e?.response?.data?.detail || 'Could not load suggested references.');
    }
  }, [siteId, showError]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const adopt = async () => {
    setBusy(true);
    try {
      const { data: res } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/legal-references/catalog/adopt`,
        { catalog_ids: Array.from(picked) },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      showSuccess(`Added ${res.added} reference${res.added === 1 ? '' : 's'} to your library.`);
      setPicked(new Set());
      await load();
      onAdopted?.();
    } catch (e) {
      showError(e?.response?.data?.detail || 'Could not add the references.');
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  const available = data.suggestions.filter((s) => !s.already_added);
  if (!available.length) return null;

  return (
    <div className="border border-blue-200 bg-blue-50/50 rounded-lg mb-6">
      <button onClick={() => setOpen((o) => !o)}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left">
        <span className="flex items-center gap-2 text-sm font-medium text-blue-900">
          <Sparkles className="h-4 w-4" />
          {available.length} suggested reference{available.length === 1 ? '' : 's'}
          {data.industry ? ` for ${data.industry.replace(/_/g, ' ')}` : ''}
        </span>
        <span className="text-xs text-blue-700">{open ? 'Hide' : 'Review'}</span>
      </button>

      {open && (
        <div className="px-4 pb-4">
          <p className="text-xs text-gray-600 mb-3">
            The whole library is offered: a statutory duty follows what a site has,
            not what it makes. Entries typical for your industry are highlighted.
          </p>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {available.map((s) => (
              <label key={s.id}
                     className="flex items-start gap-3 bg-white border border-gray-200
                                rounded-lg p-3 cursor-pointer">
                <input type="checkbox" className="mt-1" disabled={!data.can_manage}
                       checked={picked.has(s.id)} onChange={() => toggle(s.id)} />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-gray-900">{s.title}</span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    {s.authority} · {s.category}
                  </span>
                  <span className="block text-xs text-gray-600 mt-1">{s.description}</span>
                  {s.reasons?.length > 0 && (
                    <span className="block text-xs text-blue-700 mt-1">
                      {s.reasons.join(' · ')}
                    </span>
                  )}
                </span>
              </label>
            ))}
          </div>
          {data.can_manage ? (
            <button onClick={adopt} disabled={!picked.size || busy}
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-blue-600
                               text-white rounded-lg text-sm hover:bg-blue-700
                               disabled:opacity-50">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Add {picked.size || ''} to library
            </button>
          ) : (
            <p className="text-xs text-gray-500 mt-3">
              Adding a reference needs the legal-admin permission.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const ComplianceRegister = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { showError, showSuccess, confirm } = useAlert();
  const { activeSiteId, availableSites } = useSite();

  // ?tab= lets the retired /safety/legal-references path land on the
  // References tab instead of dumping the visitor on the register. Unknown
  // values fall back rather than rendering an empty page.
  const [tab, setTab] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get('tab');
    return ['register', 'obligations', 'applicable', 'references'].includes(requested)
      ? requested
      : 'register';
  });
  const [statusFilter, setStatusFilter] = useState('');
  // Bumped after adopting suggestions, to remount the embedded library.
  const [refsVersion, setRefsVersion] = useState(0);
  const [register, setRegister] = useState({ summary: {}, events: [] });
  const [obligations, setObligations] = useState([]);
  // The insight layer over the register: exposure, lookahead, coverage.
  const [insights, setInsights] = useState(null);
  // Applicability: the whole library, with this site's industry highlighted.
  // There is no site questionnaire any more — see services/compliance_catalog.
  const [proposal, setProposal] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Obligation being created or edited. `null` means the form is closed.
  const [obligationForm, setObligationForm] = useState(null);
  // Event whose outcome is being recorded.
  const [outcomeFor, setOutcomeFor] = useState(null);
  const [outcome, setOutcome] = useState({
    kind: 'met', completed_date: '', evidence_document_id: '',
    non_compliance_notes: '', capa_id: '', notes: '',
  });

  const siteId = activeSiteId || '';
  const needsSiteChoice = !siteId && availableSites.length > 1;

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError(null);
    try {
      const [reg, obs, ins] = await Promise.all([
        fetchRegister({ siteId: siteId || undefined, status: statusFilter || undefined },
          { signal }),
        fetchObligations({ siteId: siteId || undefined, includeInactive: true }, { signal }),
        // Fail-soft: the insight band is additive, so a failure here must not
        // cost the register itself.
        fetchRegisterInsights({ siteId: siteId || undefined }, { signal })
          .catch(() => null),
      ]);
      setRegister(reg);
      setObligations(obs);
      setInsights(ins);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      const status = err?.response?.status;
      setError(
        status === 403
          ? 'You do not have permission to view the compliance register.'
          : err?.response?.data?.detail || 'Could not load the compliance register.',
      );
    } finally {
      setLoading(false);
    }
  }, [siteId, statusFilter]);

  useEffect(() => {
    if (needsSiteChoice) return undefined;
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load, needsSiteChoice]);

  // Applicability is loaded on demand: it is a second round trip and most
  // register visits never open the tab.
  const loadApplicability = useCallback(async (signal) => {
    if (!siteId) return;
    try {
      const prop = await fetchApplicableCatalog({ siteId }, { signal });
      setProposal(prop);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      showError(err?.response?.data?.detail
        || 'Could not load the applicable-requirements library.');
    }
  }, [siteId, showError]);

  useEffect(() => {
    if (tab !== 'applicable' || needsSiteChoice || proposal) return undefined;
    const controller = new AbortController();
    loadApplicability(controller.signal);
    return () => controller.abort();
  }, [tab, needsSiteChoice, proposal, loadApplicability]);


  const adoptSelected = async () => {
    const ids = [...selected];
    if (!ids.length) { showError('Select at least one requirement.'); return; }
    setSaving(true);
    try {
      const res = await adoptCatalogEntries({ siteId, catalogIds: ids });
      const made = res.created?.length || 0;
      showSuccess(made
        ? `${made} requirement${made === 1 ? '' : 's'} added to the register, `
          + `${res.events_created} due period${res.events_created === 1 ? '' : 's'} scheduled.`
        : 'Nothing new to add — those requirements are already in the register.');
      setSelected(new Set());
      // Reload both sides: the proposal's adopted flags and the register itself.
      await Promise.all([loadApplicability(), load()]);
    } catch (err) {
      showError(err?.response?.data?.detail || 'Could not adopt the selection.');
    } finally {
      setSaving(false);
    }
  };

  const toggleSelected = (catalogId) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(catalogId) ? next.delete(catalogId) : next.add(catalogId);
    return next;
  });

  const summary = register.summary || {};

  // Obligations with no first due date produce no periods at all. That is a
  // deliberate server-side choice (a due date is never guessed at), which makes
  // it something the page has to surface or the register silently under-reports.
  const unscheduled = useMemo(
    () => obligations.filter((o) => o.is_active && !o.first_due_date),
    [obligations],
  );

  const openNewObligation = () => setObligationForm({ ...EMPTY_OBLIGATION });

  const openEditObligation = (o) => setObligationForm({
    id: o.id,
    title: o.title || '',
    citation: o.citation || '',
    category: o.category || '',
    authority: o.authority || '',
    frequency: o.frequency || 'annual',
    frequency_days: o.frequency_days ?? '',
    first_due_date: o.first_due_date || '',
    responsible_name: o.responsible_name || '',
    evidence_required: Boolean(o.evidence_required),
    notes: o.notes || '',
    is_active: Boolean(o.is_active),
  });

  const saveObligation = async () => {
    const form = obligationForm;
    if (!form?.title?.trim()) { showError('A requirement title is required.'); return; }
    if (form.frequency === 'custom_days' && !Number(form.frequency_days)) {
      showError('Enter the number of days between occurrences.');
      return;
    }
    setSaving(true);
    try {
      const body = {
        title: form.title.trim(),
        site_id: siteId || undefined,
        citation: form.citation || null,
        category: form.category || null,
        authority: form.authority || null,
        frequency: form.frequency,
        frequency_days: form.frequency === 'custom_days'
          ? Number(form.frequency_days) : null,
        first_due_date: form.first_due_date || null,
        responsible_name: form.responsible_name || null,
        evidence_required: form.evidence_required,
        notes: form.notes || null,
        is_active: form.is_active,
      };
      const res = form.id
        ? await updateObligation(form.id, body)
        : await createObligation(body);
      setObligationForm(null);
      const created = res?.events_created || 0;
      showSuccess(created
        ? `Saved. ${created} due period${created === 1 ? '' : 's'} scheduled.`
        : 'Saved.');
      await load();
    } catch (err) {
      showError(err?.response?.data?.detail || 'Could not save the obligation.');
    } finally {
      setSaving(false);
    }
  };

  const retireObligation = async (o) => {
    const ok = await confirm(
      'Retire this requirement?',
      `"${o.title}" will stop generating new due periods. Periods already recorded `
      + 'are kept — they are the evidence of what was complied with.',
      { confirmLabel: 'Retire', destructive: true },
    );
    if (!ok) return;
    try {
      await deleteObligation(o.id, { siteId: siteId || undefined });
      showSuccess('Requirement retired.');
      await load();
    } catch (err) {
      showError(err?.response?.data?.detail || 'Could not retire the obligation.');
    }
  };

  const openOutcome = (event) => {
    setOutcomeFor(event);
    setOutcome({
      kind: event.non_compliance ? 'not_met' : 'met',
      completed_date: event.completed_date || new Date().toISOString().slice(0, 10),
      evidence_document_id: event.evidence_document_id || '',
      non_compliance_notes: '',
      capa_id: event.capa_id || '',
      notes: event.notes || '',
    });
  };

  const saveOutcome = async () => {
    if (!outcomeFor) return;
    const met = outcome.kind === 'met';
    if (met && !outcome.completed_date) {
      showError('Enter the date the requirement was met.');
      return;
    }
    setSaving(true);
    try {
      // Mutually exclusive by design, and the server rejects the combination —
      // an occurrence cannot be both met and a non-compliance.
      await recordEvent(outcomeFor.id, {
        completed_date: met ? outcome.completed_date : null,
        evidence_document_id: met ? (outcome.evidence_document_id || null) : null,
        non_compliance: !met,
        non_compliance_notes: met ? null : (outcome.non_compliance_notes || null),
        capa_id: met ? null : (outcome.capa_id || null),
        notes: outcome.notes || null,
      }, { siteId: siteId || undefined });
      setOutcomeFor(null);
      showSuccess('Outcome recorded.');
      await load();
    } catch (err) {
      showError(err?.response?.data?.detail || 'Could not record the outcome.');
    } finally {
      setSaving(false);
    }
  };

  const topUp = async () => {
    setSaving(true);
    try {
      const res = await generateEvents({ siteId: siteId || undefined });
      const n = res?.events_created || 0;
      showSuccess(n
        ? `${n} missing due period${n === 1 ? '' : 's'} created.`
        : 'Already up to date — no missing periods.');
      await load();
    } catch (err) {
      showError(err?.response?.data?.detail || 'Could not generate due periods.');
    } finally {
      setSaving(false);
    }
  };

  const events = register.events || [];

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <button onClick={() => navigate('/safety')}
                  className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-3">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Safety
          </button>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Legal &amp; Regulatory Compliance
              </h1>
              <p className="text-gray-600 mt-1">
                Statutory requirements applicable to this site, their due periods
                and evidence.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={topUp} disabled={saving || needsSiteChoice}
                      title="Create any due periods that have not been generated yet"
                      className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300
                                 rounded-lg text-sm text-gray-700 hover:bg-gray-50
                                 disabled:opacity-50">
                <RefreshCw className={`h-4 w-4 ${saving ? 'animate-spin' : ''}`} />
                Refresh schedule
              </button>
              <button onClick={openNewObligation} disabled={needsSiteChoice}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white
                                 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                <Plus className="h-4 w-4" />
                Add requirement
              </button>
            </div>
          </div>
        </div>

        {needsSiteChoice && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">
              Pick a site in the header bar. A statutory requirement applies to one
              site, so the register is always site-specific.
            </p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {!needsSiteChoice && (
          <>
            <ExposureBand insights={insights} />
            <InsightFindings insights={insights}
                             onJumpToCatalog={() => setTab('applicable')} />

            {/* Headline position. Compliance rate counts only periods that have
                actually come due — a requirement due next March is not a miss. */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
              <SummaryCard label="Requirements" value={obligations.filter((o) => o.is_active).length} />
              <SummaryCard label="Overdue" value={summary.overdue} tone="rose" />
              <SummaryCard label="Due in 30 days" value={summary.due_soon} tone="amber" />
              <SummaryCard label="Evidence missing" value={summary.evidence_missing}
                           tone="amber" hint="Done, unprovable" />
              <SummaryCard label="Non-compliances" value={summary.not_met} tone="rose" />
              <SummaryCard label="Compliance rate"
                           value={summary.compliance_rate === null
                             || summary.compliance_rate === undefined
                             ? null : `${summary.compliance_rate}%`}
                           hint="Of periods already due" />
            </div>

            {unscheduled.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                <FileWarning className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  {unscheduled.length} requirement{unscheduled.length === 1 ? '' : 's'} ha
                  {unscheduled.length === 1 ? 's' : 've'} no first due date, so no due
                  periods are being tracked for {unscheduled.length === 1 ? 'it' : 'them'}.
                  Set a first due date to start the schedule.
                </p>
              </div>
            )}

            <div className="flex items-center gap-1 mb-4 border-b border-gray-200">
              {[
                { id: 'applicable', label: 'What applies to us', Icon: Gavel },
                { id: 'obligations', label: 'Requirements', Icon: CheckCircle2 },
                { id: 'register', label: 'Register', Icon: ClipboardList },
                { id: 'references', label: 'Legal References', Icon: Scale },
              ].map(({ id, label, Icon }) => (
                <button key={id} onClick={() => setTab(id)}
                        className={`inline-flex items-center gap-2 px-4 py-2 text-sm border-b-2
                                    -mb-px ${tab === id
                                      ? 'border-blue-600 text-blue-700 font-medium'
                                      : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>

            {tab === 'register' && <LookaheadPanels insights={insights} />}

            {tab === 'register' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="p-4 flex flex-wrap items-center gap-2 border-b border-gray-200">
                  <button onClick={() => setStatusFilter('')}
                          className={`px-3 py-1 rounded-full text-xs border ${statusFilter === ''
                            ? 'bg-gray-900 text-white border-gray-900'
                            : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                    All
                  </button>
                  {EVENT_STATUSES.map((s) => (
                    <button key={s.id} onClick={() => setStatusFilter(s.id)}
                            className={`px-3 py-1 rounded-full text-xs border
                                        ${statusFilter === s.id
                                          ? 'bg-gray-900 text-white border-gray-900'
                                          : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                      {s.label}
                    </button>
                  ))}
                  {loading && <Loader2 className="h-4 w-4 animate-spin text-gray-400 ml-auto" />}
                </div>

                {events.length === 0 && !loading ? (
                  <div className="p-8 text-center text-sm text-gray-500">
                    {statusFilter
                      ? 'No due periods in this state.'
                      : 'No due periods yet. Add a requirement with a first due date '
                        + 'and its schedule is generated automatically.'}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                        <tr>
                          <th className="px-4 py-2 text-left">Requirement</th>
                          <th className="px-4 py-2 text-left">Period</th>
                          <th className="px-4 py-2 text-left">Due</th>
                          <th className="px-4 py-2 text-left">Owner</th>
                          <th className="px-4 py-2 text-left">Status</th>
                          <th className="px-4 py-2 text-left">Evidence</th>
                          <th className="px-4 py-2 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {events.map((e) => {
                          const days = daysUntil(e.due_date);
                          const open = !e.completed_date && !e.non_compliance;
                          return (
                            <tr key={e.id} className="hover:bg-gray-50">
                              <td className="px-4 py-2">
                                <div className="text-gray-900">{e.title}</div>
                                <div className="text-[11px] text-gray-500">
                                  {[e.citation, e.category].filter(Boolean).join(' · ') || '—'}
                                </div>
                              </td>
                              <td className="px-4 py-2 text-gray-700 whitespace-nowrap">
                                {e.period_label || '—'}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap">
                                <div className="text-gray-900">{fmtDate(e.due_date)}</div>
                                {open && days !== null && (
                                  <div className={`text-[11px] ${days < 0
                                    ? 'text-rose-600' : 'text-gray-400'}`}>
                                    {days < 0
                                      ? `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} late`
                                      : `in ${days} day${days === 1 ? '' : 's'}`}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-2 text-gray-700">
                                {e.responsible_name || '—'}
                              </td>
                              <td className="px-4 py-2"><StatusChip status={e.status} /></td>
                              <td className="px-4 py-2 text-gray-600 text-xs">
                                {e.evidence_document_id
                                  ? e.evidence_document_id
                                  : (e.evidence_required
                                    ? <span className="text-amber-700">Required</span>
                                    : <span className="text-gray-400">Not required</span>)}
                              </td>
                              <td className="px-4 py-2 text-right">
                                <button onClick={() => openOutcome(e)}
                                        className="text-blue-600 hover:text-blue-800 text-xs font-medium">
                                  {e.completed_date || e.non_compliance ? 'Amend' : 'Record'}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {tab === 'obligations' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                {obligations.length === 0 && !loading ? (
                  <div className="p-8 text-center text-sm text-gray-500">
                    No requirements declared for this site yet.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                        <tr>
                          <th className="px-4 py-2 text-left">Requirement</th>
                          <th className="px-4 py-2 text-left">Category</th>
                          <th className="px-4 py-2 text-left">Frequency</th>
                          <th className="px-4 py-2 text-left">First due</th>
                          <th className="px-4 py-2 text-left">Owner</th>
                          <th className="px-4 py-2 text-left">Evidence</th>
                          <th className="px-4 py-2 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {obligations.map((o) => (
                          <tr key={o.id} className={`hover:bg-gray-50 ${o.is_active
                            ? '' : 'opacity-60'}`}>
                            <td className="px-4 py-2">
                              <div className="text-gray-900">
                                {o.title}
                                {!o.is_active && (
                                  <span className="ml-2 px-1.5 py-0.5 rounded bg-gray-100
                                                   text-gray-500 text-[10px]">Retired</span>
                                )}
                              </div>
                              <div className="text-[11px] text-gray-500">
                                {[o.citation, o.authority].filter(Boolean).join(' · ') || '—'}
                              </div>
                            </td>
                            <td className="px-4 py-2 text-gray-700">{o.category || '—'}</td>
                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap">
                              {o.frequency === 'custom_days'
                                ? `Every ${o.frequency_days} days`
                                : (FREQUENCY_LABEL[o.frequency] || o.frequency)}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              {o.first_due_date
                                ? <span className="text-gray-900">{fmtDate(o.first_due_date)}</span>
                                : <span className="text-amber-700 text-xs">Not scheduled</span>}
                            </td>
                            <td className="px-4 py-2 text-gray-700">
                              {o.responsible_name || '—'}
                            </td>
                            <td className="px-4 py-2 text-gray-600 text-xs">
                              {o.evidence_required ? 'Required' : 'Not required'}
                            </td>
                            <td className="px-4 py-2 text-right whitespace-nowrap">
                              <button onClick={() => openEditObligation(o)}
                                      className="text-blue-600 hover:text-blue-800 text-xs
                                                 font-medium mr-3">
                                Edit
                              </button>
                              {o.is_active && (
                                <button onClick={() => retireObligation(o)}
                                        title="Retire"
                                        className="text-gray-400 hover:text-rose-600">
                                  <Trash2 className="h-4 w-4 inline" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ---- What applies to us: the library, filtered by this site ---- */}
            {tab === 'applicable' && (
              <div className="space-y-4">
                {!proposal && (
                  <div className="flex items-center justify-center py-16 text-gray-500">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    Working out what applies to this site…
                  </div>
                )}

                {proposal && (
                  <>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4
                                    flex items-start gap-3">
                      <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-blue-900">
                        <p>{proposal.disclaimer}</p>
                        <p className="text-xs text-blue-700 mt-1">
                          Highlighted for industry
                          {' '}<span className="font-medium">
                            {proposal.context?.industry || 'not set'}
                          </span>
                          {proposal.context?.state &&
                            <> · state <span className="font-medium">
                              {proposal.context.state}
                            </span></>}
                          {' '}· library {proposal.catalog_version}
                        </p>
                      </div>
                    </div>

                    {/* Coverage, stated before the list: the gap is the point. */}
                    {proposal.coverage && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <SummaryCard label="Appears applicable"
                                     value={proposal.coverage.applicable} />
                        <SummaryCard label="In the register"
                                     value={proposal.coverage.tracked} />
                        <SummaryCard label="Not tracked"
                                     value={proposal.coverage.untracked} tone="rose" />
                        <SummaryCard label="Coverage"
                                     value={proposal.coverage.coverage_pct === null
                                       || proposal.coverage.coverage_pct === undefined
                                       ? null : `${proposal.coverage.coverage_pct}%`}
                                     hint="Weighted by severity" />
                      </div>
                    )}

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                      <div className="px-5 py-3 border-b border-gray-200 flex flex-wrap
                                      items-center gap-3">
                        <h2 className="font-semibold text-gray-900">
                          Proposed requirements
                        </h2>
                        <span className="text-xs text-gray-500">
                          select what applies and add it to the register
                        </span>
                        <div className="ml-auto flex items-center gap-2">
                          <button
                            onClick={() => setSelected(new Set(
                              (proposal.entries || [])
                                .filter((e) => !e.adopted && e.applies)
                                .map((e) => e.catalog_id)))}
                            className="text-xs font-medium text-blue-600 hover:text-blue-800">
                            Select all untracked
                          </button>
                          <button onClick={adoptSelected}
                                  disabled={saving || selected.size === 0}
                                  className="inline-flex items-center gap-2 px-4 py-2
                                             bg-blue-600 text-white rounded-lg text-sm
                                             hover:bg-blue-700 disabled:opacity-50">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" />
                              : <Plus className="h-4 w-4" />}
                            Add {selected.size || ''} to register
                          </button>
                        </div>
                      </div>

                      <ul className="divide-y divide-gray-100">
                        {(proposal.entries || []).map((e) => {
                          const sev = SEVERITY_META[e.severity] || SEVERITY_META.medium;
                          return (
                            <li key={e.catalog_id}
                                className={`px-5 py-3 flex items-start gap-3
                                            ${e.adopted ? 'bg-gray-50/60'
                                              : e.industry_match ? '' : 'bg-gray-50/40'}`}>
                              <input type="checkbox" className="mt-1"
                                     disabled={e.adopted}
                                     checked={selected.has(e.catalog_id)}
                                     onChange={() => toggleSelected(e.catalog_id)} />
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className={`px-1.5 py-0.5 rounded text-[10px]
                                                    font-semibold ${sev.cls}`}
                                        title={sev.hint}>
                                    {sev.label}
                                  </span>
                                  <span className="text-sm font-medium text-gray-900">
                                    {e.title}
                                  </span>
                                  {e.adopted && (
                                    <span className="px-1.5 py-0.5 rounded bg-emerald-100
                                                     text-emerald-800 text-[10px] font-medium">
                                      In register
                                    </span>
                                  )}
                                  {/* Highlight, not a gate. `missing_attributes`
                                      went with the site questionnaire — an entry
                                      is no longer excluded, it is simply not
                                      typical for this industry. */}
                                  {e.baseline ? (
                                    <span className="px-1.5 py-0.5 rounded bg-slate-100
                                                     text-slate-600 text-[10px] font-medium">
                                      Every site
                                    </span>
                                  ) : e.industry_match ? (
                                    <span className="px-1.5 py-0.5 rounded bg-blue-100
                                                     text-blue-800 text-[10px] font-medium">
                                      Typical for your industry
                                    </span>
                                  ) : null}
                                </div>
                                <div className="text-xs text-gray-600 mt-0.5">
                                  {e.citation}
                                </div>
                                {/* Why it matters, then why we think it applies —
                                    the second is what a user argues with. */}
                                <div className="text-xs text-gray-500 mt-1">
                                  {e.consequence}
                                </div>
                                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1
                                                text-[11px] text-gray-400">
                                  <span>{FREQUENCY_LABEL[e.frequency] || e.frequency}</span>
                                  <span>{e.authority}</span>
                                  {e.typical_owner && <span>usually {e.typical_owner}</span>}
                                  {e.reasons?.length > 0 && (
                                    <span className="text-gray-500">
                                      applies because: {e.reasons.join(', ')}
                                    </span>
                                  )}
                                </div>
                                {e.state_note && (
                                  <div className="text-[11px] text-amber-700 mt-1">
                                    {e.state_note}
                                  </div>
                                )}
                                {e.notes && (
                                  <div className="text-[11px] text-gray-500 mt-1">
                                    {e.notes}
                                  </div>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                      {(proposal.entries || []).length === 0 && (
                        <div className="p-8 text-center text-sm text-gray-500">
                          Nothing in the library matches this site yet. Confirm the
                          facts above — most requirements depend on them.
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* The legal library, folded in from what used to be a separate
                Safety Hub module. Same purpose as the rest of this page —
                identify what applies, cite the law behind it, follow it up —
                so it belongs behind the same tabs rather than a second card.
                Write access is still governed by the legal-admin roles the
                endpoint enforces, which is a different grant from the
                reports:create used by the other tabs; the embedded component
                reads its own can_manage from the server. */}
            {tab === 'references' && (
              <>
                <SuggestedReferences
                  siteId={activeSiteId}
                  onAdopted={() => setRefsVersion((v) => v + 1)}
                />
                {/* Remount after an adopt so the library list re-reads rather
                    than showing a stale set the user just added to. */}
                <LegalReferences key={refsVersion} user={user} onLogout={onLogout} embedded
                                 // Adding requirements from a reference changes the
                                 // register, so refresh it rather than leaving a
                                 // stale count on the tab the user came from.
                                 onRegisterChanged={() => { load(); loadApplicability(); }} />
              </>
            )}
          </>
        )}
      </div>

      {obligationForm && (
        <Modal
          wide
          title={obligationForm.id ? 'Edit requirement' : 'Add requirement'}
          onClose={() => setObligationForm(null)}
          footer={(
            <>
              <button onClick={() => setObligationForm(null)}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700">
                Cancel
              </button>
              <button onClick={saveObligation} disabled={saving}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white
                                 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Save
              </button>
            </>
          )}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Field label="Requirement" required
                     hint="What has to be done, as your legal register words it.">
                <input className={inputCls} value={obligationForm.title}
                       onChange={(e) => setObligationForm(
                         (f) => ({ ...f, title: e.target.value }))}
                       placeholder="e.g. Monthly stack emission monitoring" />
              </Field>
            </div>
            <Field label="Citation" hint="The Act, Rule or licence condition.">
              <input className={inputCls} value={obligationForm.citation}
                     onChange={(e) => setObligationForm(
                       (f) => ({ ...f, citation: e.target.value }))}
                     placeholder="e.g. Air Act 1981, s.21" />
            </Field>
            <Field label="Authority">
              <input className={inputCls} value={obligationForm.authority}
                     onChange={(e) => setObligationForm(
                       (f) => ({ ...f, authority: e.target.value }))}
                     placeholder="e.g. State Pollution Control Board" />
            </Field>
            <Field label="Category">
              <input className={inputCls} value={obligationForm.category} list="cr-categories"
                     onChange={(e) => setObligationForm(
                       (f) => ({ ...f, category: e.target.value }))} />
              <datalist id="cr-categories">
                {CATEGORY_SUGGESTIONS.map((c) => <option key={c} value={c} />)}
              </datalist>
            </Field>
            <Field label="Owner" hint="Who is accountable at this site.">
              <input className={inputCls} value={obligationForm.responsible_name}
                     onChange={(e) => setObligationForm(
                       (f) => ({ ...f, responsible_name: e.target.value }))} />
            </Field>
            <Field label="Frequency" required>
              <select className={inputCls} value={obligationForm.frequency}
                      onChange={(e) => setObligationForm(
                        (f) => ({ ...f, frequency: e.target.value }))}>
                {FREQUENCIES.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select>
            </Field>
            {obligationForm.frequency === 'custom_days' ? (
              <Field label="Days between occurrences" required>
                <input type="number" min="1" max="3650" className={inputCls}
                       value={obligationForm.frequency_days}
                       onChange={(e) => setObligationForm(
                         (f) => ({ ...f, frequency_days: e.target.value }))} />
              </Field>
            ) : <div />}
            <Field label="First due date"
                   hint="Leave blank to record the requirement without scheduling it. No due periods are tracked until this is set.">
              <input type="date" className={inputCls} value={obligationForm.first_due_date}
                     onChange={(e) => setObligationForm(
                       (f) => ({ ...f, first_due_date: e.target.value }))} />
            </Field>
            <div className="flex items-end gap-4">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={obligationForm.evidence_required}
                       onChange={(e) => setObligationForm(
                         (f) => ({ ...f, evidence_required: e.target.checked }))} />
                Evidence required
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={obligationForm.is_active}
                       onChange={(e) => setObligationForm(
                         (f) => ({ ...f, is_active: e.target.checked }))} />
                Active
              </label>
            </div>
            <div className="sm:col-span-2">
              <Field label="Notes">
                <textarea className={inputCls} rows="2" value={obligationForm.notes}
                          onChange={(e) => setObligationForm(
                            (f) => ({ ...f, notes: e.target.value }))} />
              </Field>
            </div>
          </div>
          <p className="text-[11px] text-gray-400 mt-4">
            Changing a frequency does not rewrite periods already recorded — new
            occurrences follow the new schedule, so a report issued earlier keeps
            asserting what it asserted.
          </p>
        </Modal>
      )}

      {outcomeFor && (
        <Modal title="Record outcome" onClose={() => setOutcomeFor(null)}
               footer={(
                 <>
                   <button onClick={() => setOutcomeFor(null)}
                           className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700">
                     Cancel
                   </button>
                   <button onClick={saveOutcome} disabled={saving}
                           className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600
                                      text-white rounded-lg text-sm hover:bg-blue-700
                                      disabled:opacity-50">
                     {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                     Save
                   </button>
                 </>
               )}>
          <div className="mb-4">
            <div className="text-sm font-medium text-gray-900">{outcomeFor.title}</div>
            <div className="text-xs text-gray-500">
              {outcomeFor.period_label} · due {fmtDate(outcomeFor.due_date)}
            </div>
          </div>

          <div className="flex gap-2 mb-4">
            {[
              { id: 'met', label: 'Met' },
              { id: 'not_met', label: 'Not met (non-compliance)' },
            ].map((k) => (
              <button key={k.id} onClick={() => setOutcome((o) => ({ ...o, kind: k.id }))}
                      className={`px-3 py-1.5 rounded-lg text-sm border ${outcome.kind === k.id
                        ? 'bg-gray-900 text-white border-gray-900'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                {k.label}
              </button>
            ))}
          </div>

          {outcome.kind === 'met' ? (
            <div className="space-y-4">
              <Field label="Date met" required
                     hint="Later than the due date is recorded as met late, not as met.">
                <input type="date" className={inputCls} value={outcome.completed_date}
                       onChange={(e) => setOutcome(
                         (o) => ({ ...o, completed_date: e.target.value }))} />
              </Field>
              <Field label="Evidence document"
                     hint={outcomeFor.evidence_required
                       ? 'A Document Control id. Without it this is reported as evidence missing — complied in practice, unprovable on audit.'
                       : 'Optional for this requirement.'}>
                <input className={inputCls} value={outcome.evidence_document_id}
                       onChange={(e) => setOutcome(
                         (o) => ({ ...o, evidence_document_id: e.target.value }))}
                       placeholder="Document id" />
              </Field>
            </div>
          ) : (
            <div className="space-y-4">
              <Field label="What was not met" required={false}>
                <textarea className={inputCls} rows="2" value={outcome.non_compliance_notes}
                          onChange={(e) => setOutcome(
                            (o) => ({ ...o, non_compliance_notes: e.target.value }))} />
              </Field>
              <Field label="CAPA"
                     hint="Link the corrective action. A non-compliance with nothing raised against it shows as a gap in the MIS.">
                <input className={inputCls} value={outcome.capa_id}
                       onChange={(e) => setOutcome((o) => ({ ...o, capa_id: e.target.value }))}
                       placeholder="CAPA id" />
              </Field>
            </div>
          )}

          <div className="mt-4">
            <Field label="Notes">
              <textarea className={inputCls} rows="2" value={outcome.notes}
                        onChange={(e) => setOutcome((o) => ({ ...o, notes: e.target.value }))} />
            </Field>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ComplianceRegister;
